import { ref, nextTick, onBeforeUnmount } from 'vue'
import { getSessionList } from '@/api/new-agent/qca.js'

export const useSessionList = ({ getIsTop, onLoaded, onUpdated }) => {
  const recentSessions = ref([])
  const sessionPage = ref(1)
  const sessionPageSize = 20
  const sessionsLoading = ref(false)
  const sessionsFinished = ref(false)
  let sessionsRequest = null
  let isUnmounted = false

  const mergeFirstPageSessions = (sessionList) => {
    if (sessionList.length < sessionPageSize) {
      recentSessions.value = sessionList
      sessionPage.value = 1
      return
    }
    const firstPageIds = new Set(sessionList.map((session) => session.sessionId))
    recentSessions.value = [
      ...sessionList,
      ...recentSessions.value.filter((session) => !firstPageIds.has(session.sessionId))
    ]
  }

  const appendSessions = (sessionList) => {
    const knownIds = new Set(recentSessions.value.map((session) => session.sessionId))
    sessionList.forEach((session) => {
      if (knownIds.has(session.sessionId)) return
      knownIds.add(session.sessionId)
      recentSessions.value.push(session)
    })
  }

  const loadSessions = (targetPage = sessionPage.value, refreshFirstPage = false) => {
    sessionsLoading.value = true
    sessionsRequest = (async () => {
      try {
        const res = await getSessionList({
          page: targetPage,
          pageSize: sessionPageSize,
          isTop: getIsTop()
        })
        if (isUnmounted) return false
        const sessionList = res.result?.sessionList || []
        if (refreshFirstPage) {
          mergeFirstPageSessions(sessionList)
        } else {
          appendSessions(sessionList)
          sessionPage.value = targetPage
        }
        onLoaded(recentSessions.value)
        const rowsCount = res.result?.pageHelper?.rowsCount
        sessionsFinished.value =
          (!refreshFirstPage && sessionList.length === 0) ||
          (rowsCount != null
            ? recentSessions.value.length >= rowsCount
            : sessionList.length < sessionPageSize)
        return true
      } catch (error) {
        console.error('获取会话列表失败：', error)
        return false
      } finally {
        sessionsLoading.value = false
        await nextTick()
        onUpdated()
      }
    })()
    return sessionsRequest
  }

  const refreshFirstPageSessions = async () => {
    if (isUnmounted) return
    try {
      // 等待当前分页请求完成，避免第一页刷新与加载更多覆盖彼此的结果。
      await sessionsRequest
      if (!isUnmounted) await loadSessions(1, true)
    } finally {
      await nextTick()
      onUpdated()
    }
  }

  const handleLoadMore = () => {
    if (isUnmounted || sessionsLoading.value || sessionsFinished.value) return
    return loadSessions(sessionPage.value + 1)
  }

  const removeLocalSession = (id) => {
    recentSessions.value = recentSessions.value.filter((session) => session.sessionId !== id)
  }

  const waitForPendingRequests = () => sessionsRequest
  onBeforeUnmount(() => {
    isUnmounted = true
  })
  return {
    recentSessions,
    sessionsLoading,
    sessionsFinished,
    loadSessions,
    refreshFirstPageSessions,
    loadMore: handleLoadMore,
    removeLocalSession,
    waitForPendingRequests
  }
}
